import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { Layout } from "@/components/Layout";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Users, Building, Edit, Search, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const getRoleBadgeColor = (role) => {
  switch (role) {
    case 'superadmin':
      return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'clinic_admin':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'nurse':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'doctor':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const formatRoleName = (role) => {
  if (role === 'clinic_admin') return 'Clinic Admin';
  return role.charAt(0).toUpperCase() + role.slice(1);
};

export const UserManagementPage = () => {
  const { user } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [filteredClinics, setFilteredClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [createClinicDialogOpen, setCreateClinicDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  // Search states
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [clinicSearchQuery, setClinicSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  
  const [userFormData, setUserFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "",
    clinic_id: ""
  });
  
  const [clinicFormData, setClinicFormData] = useState({
    name: "",
    address: "",
    phones: "",
    email: "",
    website: "",
    services: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Filter users based on search query
    if (userSearchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const query = userSearchQuery.toLowerCase();
      const filtered = users.filter(u =>
        (u.full_name && u.full_name.toLowerCase().includes(query)) ||
        (u.email && u.email.toLowerCase().includes(query)) ||
        (u.role && u.role.toLowerCase().includes(query))
      );
      setFilteredUsers(filtered);
    }
  }, [userSearchQuery, users]);

  useEffect(() => {
    // Filter clinics based on search query
    if (clinicSearchQuery.trim() === '') {
      setFilteredClinics(clinics);
    } else {
      const query = clinicSearchQuery.toLowerCase();
      const filtered = clinics.filter(c =>
        (c.name && c.name.toLowerCase().includes(query)) ||
        (c.address && c.address.toLowerCase().includes(query)) ||
        (c.email && c.email.toLowerCase().includes(query))
      );
      setFilteredClinics(filtered);
    }
  }, [clinicSearchQuery, clinics]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, clinicsData] = await Promise.all([
        api.get("/auth/users"),
        api.get("/clinics")
      ]);
      // Sort alphabetically
      const sortedUsers = usersData.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      const sortedClinics = clinicsData.sort((a, b) => a.name.localeCompare(b.name));
      
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
      setClinics(sortedClinics);
      setFilteredClinics(sortedClinics);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // For clinic admins, automatically set their clinic_id
      const userData = {
        ...userFormData,
        clinic_id: isClinicAdmin ? user?.clinic_id : userFormData.clinic_id
      };
      await api.post("/auth/users", userData);
      toast.success("User created successfully!");
      setCreateUserDialogOpen(false);
      setUserFormData({ email: "", password: "", full_name: "", role: "", clinic_id: "" });
      await loadData();
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleEditUser = (userData) => {
    setSelectedUser(userData);
    setUserFormData({
      email: userData.email,
      password: "",
      full_name: userData.full_name || "",
      role: userData.role,
      clinic_id: userData.clinic_id || ""
    });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        full_name: userFormData.full_name,
        email: userFormData.email,
        role: userFormData.role
      };
      
      // Only include password if it's provided
      if (userFormData.password) {
        updateData.password = userFormData.password;
      }

      await api.patch(`/users/${selectedUser.id}`, updateData);
      toast.success("User updated successfully!");
      setEditUserDialogOpen(false);
      setSelectedUser(null);
      setUserFormData({ email: "", password: "", full_name: "", role: "", clinic_id: "" });
      await loadData();
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleCreateClinic = async (e) => {
    e.preventDefault();
    try {
      const clinicData = {
        ...clinicFormData,
        phones: clinicFormData.phones.split(",").map(p => p.trim()),
        services: clinicFormData.services.split(",").map(s => s.trim()).filter(s => s)
      };
      await api.post("/clinics/", clinicData);
      toast.success("Clinic created successfully!");
      setCreateClinicDialogOpen(false);
      setClinicFormData({ name: "", address: "", phones: "", email: "", website: "", services: "" });
      await loadData();
    } catch (error) {
      console.error("Failed to create clinic:", error);
      toast.error(error.response?.data?.detail || "Failed to create clinic");
    }
  };

  const confirmDeleteUser = (userEmail) => {
    setUserToDelete(userEmail);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/auth/users/${userToDelete}`);
      toast.success("User deleted successfully!");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await loadData();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const getClinicName = (clinicId) => {
    const clinic = clinics.find(c => c.id === clinicId);
    return clinic ? clinic.name : "N/A";
  };

  // Role-based access control
  const isSuperadmin = user?.role === "superadmin";
  const isClinicAdmin = user?.role === "clinic_admin";

  if (!isSuperadmin && !isClinicAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#fcfbf9" }}>
        <Card className="rounded-2xl border-[#e5e5e5] p-8">
          <p style={{ color: "#555555" }}>Access Denied: You don't have permission to view this page.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-4 rounded-xl" style={{ backgroundColor: "#652D90" }}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <h1 className="text-4xl font-bold mb-8" style={{ color: "#652D90", fontFamily: "Poppins, sans-serif" }}>
          User Management
        </h1>

        {/* Tabs for Users and Clinics */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="users" className="rounded-xl">
              <Users size={18} className="mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="clinics" className="rounded-xl">
              <Building size={18} className="mr-2" />
              Clinics
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="rounded-2xl border-[#e5e5e5]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl flex items-center gap-2" style={{ color: "#652D90" }}>
                    <Users size={24} />
                    Users
                  </CardTitle>
                  <Button
                    onClick={() => {
                      setUserFormData({
                        email: "",
                        password: "",
                        full_name: "",
                        role: "",
                        clinic_id: ""
                      });
                      setCreateUserDialogOpen(true);
                    }}
                    className="rounded-xl"
                    style={{ backgroundColor: "#652D90" }}
                  >
                    <Plus size={16} className="mr-2" />
                    Add User
                  </Button>
                </div>
                {/* Search Input */}
                <div className="mt-4 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Search users by name, email, or role..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p style={{ color: "#555555" }}>Loading users...</p>
                ) : filteredUsers.length === 0 ? (
                  <p style={{ color: "#555555" }}>
                    {userSearchQuery ? 'No users found matching your search.' : 'No users yet. Create the first user.'}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b" style={{ backgroundColor: "#f8f6f4" }}>
                          <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: "#333333" }}>Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: "#333333" }}>Email</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: "#333333" }}>Role</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: "#333333" }}>Clinic</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold" style={{ color: "#333333" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((u) => (
                          <tr key={u.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm" style={{ color: "#555555" }}>{u.full_name}</td>
                            <td className="px-4 py-3 text-sm" style={{ color: "#555555" }}>{u.email}</td>
                            <td className="px-4 py-3">
                              <Badge className={getRoleBadgeColor(u.role)}>
                                {formatRoleName(u.role)}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm" style={{ color: "#555555" }}>
                              {clinics.find(c => c.id === u.clinic_id)?.name || "N/A"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditUser(u)}
                                  className="rounded-lg"
                                >
                                  <Edit size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteUser(u)}
                                  className="rounded-lg text-red-600 hover:text-red-700"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clinics Tab */}
          <TabsContent value="clinics">
            <Card className="rounded-2xl border-[#e5e5e5]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl flex items-center gap-2" style={{ color: "#652D90" }}>
                    <Building size={24} />
                    Clinics
                  </CardTitle>
                  {isSuperadmin && (
                    <Button
                      onClick={() => setCreateClinicDialogOpen(true)}
                      className="rounded-xl"
                      style={{ backgroundColor: "#652D90" }}
                    >
                      <Plus size={16} className="mr-2" />
                      Add Clinic
                    </Button>
                  )}
                </div>
                {/* Search Input */}
                <div className="mt-4 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Search clinics by name, address, or email..."
                    value={clinicSearchQuery}
                    onChange={(e) => setClinicSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p style={{ color: "#555555" }}>Loading clinics...</p>
                ) : filteredClinics.length === 0 ? (
                  <p style={{ color: "#555555" }}>
                    {clinicSearchQuery ? 'No clinics found matching your search.' : 'No clinics yet. Create the first clinic.'}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredClinics.map((clinic) => (
                      <Card key={clinic.id} className="rounded-xl border-[#e5e5e5]">
                        <CardContent className="pt-4">
                          <p className="font-semibold text-base mb-2" style={{ color: "#333333" }}>{clinic.name}</p>
                          <p className="text-sm mb-1" style={{ color: "#555555" }}>{clinic.address}</p>
                          <p className="text-sm" style={{ color: "#555555" }}>{clinic.email}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Users Section (Removed - Now in tabs) */}
        {/* Clinics Section (Removed - Now in tabs) */}

        {/* Create User Dialog */}
        <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle style={{ color: "#652D90" }}>Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  required
                  value={userFormData.full_name}
                  onChange={(e) => setUserFormData({...userFormData, full_name: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    required
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                    className="rounded-xl pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={userFormData.role} onValueChange={(value) => setUserFormData({...userFormData, role: value})}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperadmin && <SelectItem value="clinic_admin">Clinic Admin</SelectItem>}
                    <SelectItem value="nurse">Nurse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Only show clinic dropdown for superadmin */}
              {isSuperadmin && (
                <div className="space-y-2">
                  <Label>Clinic</Label>
                  <Select value={userFormData.clinic_id} onValueChange={(value) => setUserFormData({...userFormData, clinic_id: value})}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select clinic" />
                    </SelectTrigger>
                    <SelectContent>
                      {clinics.map((clinic) => (
                        <SelectItem key={clinic.id} value={clinic.id}>
                          {clinic.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isClinicAdmin && (
                <div className="space-y-2">
                  <Label>Clinic</Label>
                  <Input
                    disabled
                    value={getClinicName(user?.clinic_id)}
                    className="rounded-xl bg-gray-100"
                  />
                </div>
              )}
              <Button type="submit" className="w-full rounded-xl" style={{ backgroundColor: "#1fb25a" }}>
                Create User
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle style={{ color: "#652D90" }}>Edit User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  required
                  value={userFormData.full_name}
                  onChange={(e) => setUserFormData({...userFormData, full_name: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Password (leave blank to keep current)</Label>
                <div className="relative">
                  <Input
                    type={showEditPassword ? "text" : "password"}
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                    placeholder="Enter new password or leave blank"
                    className="rounded-xl pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showEditPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={userFormData.role} onValueChange={(value) => setUserFormData({...userFormData, role: value})}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperadmin && <SelectItem value="clinic_admin">Clinic Admin</SelectItem>}
                    <SelectItem value="nurse">Nurse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Clinic</Label>
                <Input
                  disabled
                  value={getClinicName(selectedUser?.clinic_id)}
                  className="rounded-xl bg-gray-100"
                />
                <p className="text-xs text-gray-500">Clinic assignment cannot be changed after creation</p>
              </div>
              <Button type="submit" className="w-full rounded-xl" style={{ backgroundColor: "#1fb25a" }}>
                Update User
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Clinic Dialog */}
        <Dialog open={createClinicDialogOpen} onOpenChange={setCreateClinicDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle style={{ color: "#652D90" }}>Create New Clinic</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClinic} className="space-y-4">
              <div className="space-y-2">
                <Label>Clinic Name</Label>
                <Input
                  required
                  value={clinicFormData.name}
                  onChange={(e) => setClinicFormData({...clinicFormData, name: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  required
                  value={clinicFormData.address}
                  onChange={(e) => setClinicFormData({...clinicFormData, address: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone Numbers (comma-separated)</Label>
                <Input
                  required
                  value={clinicFormData.phones}
                  onChange={(e) => setClinicFormData({...clinicFormData, phones: e.target.value})}
                  placeholder="123-456-7890, 098-765-4321"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={clinicFormData.email}
                  onChange={(e) => setClinicFormData({...clinicFormData, email: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Website (optional)</Label>
                <Input
                  value={clinicFormData.website}
                  onChange={(e) => setClinicFormData({...clinicFormData, website: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Services (comma-separated, optional)</Label>
                <Input
                  value={clinicFormData.services}
                  onChange={(e) => setClinicFormData({...clinicFormData, services: e.target.value})}
                  placeholder="Lab Testing, X-Ray, Consultation"
                  className="rounded-xl"
                />
              </div>
              <Button type="submit" className="w-full rounded-xl" style={{ backgroundColor: "#652D90" }}>
                Create Clinic
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: "#652D90" }}>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete user <strong>{userToDelete}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="rounded-xl"
                style={{ backgroundColor: "#e90d01" }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
